import React, { useEffect, useId, useState } from 'react';
import useAutocomplete from '@mui/material/useAutocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { useForkRef } from '@mui/material/utils';
import TooltipInfo from './TooltipInfo';

// Help content for the search syntax, shown via the info icon in the field.
const searchHelp = (
    <>
        <strong>Search tips</strong>
        <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.1rem' }}>
            <li>Several terms: all must match (AND)</li>
            <li>Partial text works: factor finds factorIXa</li>
            <li>Exclude with a leading minus: factor -4YZU</li>
            <li>Exclusion only matches whole name tokens, not partial</li>
        </ul>
    </>
);

function SelectSearch({
    optionList, onSelection, label, hasLinks=false,
    defaultQuery='', checkForDynamicEntries=() => {},
    error, focusWhenSelected,
}) {
    const [query, setQuery] = useState('');
    const [value, setValue] = useState(null);
    const inputRef = React.useRef(null);

    useEffect(() => {
        if (defaultQuery !== '') {
            setQuery(defaultQuery);
            checkForDynamicEntries(defaultQuery);
        }
    }, []);

    // The bare `autoFocus` below only fires on mount, but this component is mounted while its
    // tab is hidden (display:none), so that initial focus is a no-op. Re-focus when the tab
    // becomes visible so the search box is ready to type into. undefined => opt out (leave the
    // one-shot autoFocus as the only behavior) for callers that don't track tab selection.
    //
    // Deferred by two frames on purpose. Focusing puts MUI's useAutocomplete into its focused
    // state, which scrolls the highlighted option into view via getBoundingClientRect. Called
    // synchronously as the tab is revealed, that read forces a SECOND full layout of the option
    // list -- measured at ~+800ms on top of the ~630ms the reveal already costs, with a large
    // (~14.5k node) structure list. A single rAF still runs before that frame's layout, so it
    // can force the same work; waiting for the second frame means a paint (and its layout) has
    // completed and the measurement reads cached values instead.
    // NOTE: this only avoids the DUPLICATE layout. The list is unvirtualized, so the reveal
    // itself stays expensive -- virtualizing the listbox below is the real fix.
    useEffect(() => {
        if (!focusWhenSelected) return undefined;
        let innerFrame = 0;
        const outerFrame = requestAnimationFrame(() => {
            innerFrame = requestAnimationFrame(() => inputRef.current?.focus());
        });
        return () => {
            cancelAnimationFrame(outerFrame);
            if (innerFrame) cancelAnimationFrame(innerFrame);
        };
    }, [focusWhenSelected]);

    // ⚠️ THE SAME ID HAS TO REACH BOTH THE HOOK AND THE TextField, or the field has no
    // accessible name. `getInputProps()` returns an `id` (useAutocomplete.js: `getInputProps:
    // () => ({ id, ... })`) and it is spread onto the input below, which OVERWRITES the id
    // TextField generated for itself — and TextField's `<label htmlFor>` still points at that
    // generated one. The label then labels nothing: measured, the input presented as
    // `combobox [expanded]` with no accessible name, so `getByLabel` and any name-based query
    // could not find the structure-search box at all, and the e2e helpers had to target it
    // structurally (`input[role="combobox"]`) to tell it from the Selectivity Add type Select.
    // Passing one id to both ends puts the label back on the input.
    //
    // It is per-instance rather than the literal 'use-autocomplete' it used to be: that was a
    // single hardcoded DOM id, so two SelectSearch instances mounted at once would collide.
    const autoCompleteId = useId();
    const autoCompleteHook = useAutocomplete({
        id: autoCompleteId,
        options: optionList,
        inputValue: query,
        open: true,
        // multiple: true, // disabled for now to avoid "selecting" invalid entries.
        freeSolo: true,
        value,
        onChange: async (event, newValue) => {
            setQuery(query); // keep what the user entered; don't override with the new value
            await onSelection(newValue);

            // if user cancels or there are load errors autocomplete won't trigger change event
            // reset the value to null so that pressing enter or clicking protein triggers this
            // event again.
            setValue(null);
            inputRef.current.focus();
        },
        onInputChange: (event, newInputValue, reason) => {
            if ((event?.type === 'keydown' && event.key === 'Enter') || reason === 'reset') {
                // After pressing enter, the autocomplete tooling will attempt to clear the input.
                // But we want to keep the input if it's a string that doesn't match any entry.
                // So don't update the query state on "submit".
                return;
            }
            setQuery(newInputValue);
            checkForDynamicEntries(newInputValue);
        },
        filterOptions: filterOptionsByTerms,
        getOptionLabel: (option) => option.text || '',
        groupBy: (option) => option.groupName,
    });

    // In MUI v9, getInputProps() returns a ref that must be attached via inputRef, not inputProps.
    const { ref: acInputRef, ...acInputProps } = autoCompleteHook.getInputProps();
    const mergedInputRef = useForkRef(inputRef, acInputRef);

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden', // Only the list items should scroll, not the autocomplete box.
            paddingTop: '1rem', // avoid the autocomplete help text from being cut off
        }}
        >
            <Box {...autoCompleteHook.getRootProps()} sx={{ padding: '0 0 1rem 0' }}>
                <TextField
                    variant="outlined"
                    style={{ width: '100%' }}
                    // Same id the hook puts on the input (see autoCompleteId above), so
                    // InputLabel's htmlFor resolves to the element it is labelling.
                    id={autoCompleteId}
                    label={label}
                    focused
                    autoFocus
                    inputRef={mergedInputRef}
                    slotProps={{
                        htmlInput: acInputProps,
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <TooltipInfo title={searchHelp} />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            </Box>
            { !!error && error }
            {autoCompleteHook.groupedOptions.length > 0 ? (
                <Box
                    sx={{
                        overflow: 'auto',
                        flex: 1,
                        minHeight: 0,
                        position: 'relative',
                        border: '2px solid #006428',
                        borderRadius: '.4rem',
                        '& li.Mui-focused': {
                            backgroundColor: '#4a8df6',
                            color: 'white',
                            cursor: 'pointer',
                        },
                    }}
                    {...autoCompleteHook.getListboxProps()}
                >
                    {autoCompleteHook.groupedOptions.map(({ group, options, index }) => (
                        <React.Fragment key={group}>
                            <strong style={{ padding: '0 0 0 .2rem' }}>{group}</strong>
                            {options.map((option, ind) => {
                                const { key, ...optionProps } = autoCompleteHook.getOptionProps(
                                    { option, index: (index+ind) }
                                );
                                return (
                                    <div key={option.id} title={option.title || ''} style={{ display: 'flex' }}>
                                        <li style={{ padding: '0 1rem', flexGrow: 1, listStyle: 'none' }} {...optionProps}>
                                            {option.text}
                                        </li>
                                        {hasLinks? (
                                            <div style={{ minWidth: '6rem', display: 'flex', justifyContent: 'center' }}>
                                                {option.linkInfo? (
                                                    <a href={option.linkInfo.url} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
                                                        {option.linkInfo.label}
                                                        <i className="fa fa-external-link" />
                                                    </a>
                                                ) : null}
                                            </div>
                                        ): null}
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </Box>
            ) : (
                <p>No items match your search.</p>
            )}
        </Box>
    );
}

export default SelectSearch;

// --- Multi-term search (BP2-2592) ---------------------------------------------
// Treat the query as multiple whitespace-separated terms (AND-combined) rather
// than one literal string. A leading "-" negates a term. Per Dan's call,
// INCLUSION is broad and EXCLUSION is whole-token:
//   - POSITIVE terms match as a SUBSTRING, so incremental type-ahead keeps working
//     ("bra" -> "BRAF") and "BRAF in" keeps any BRAF row whose text contains "in".
//   - A NEGATIVE "-term" excludes a row only on a WHOLE-TOKEN match: its token(s) must
//     appear as a CONSECUTIVE run of the row's tokens. A token is a maximal run of
//     letters/digits plus the project/case-name separators "_ . + -" (ProjectNameRegex),
//     so a whole name or hyphenated label ("dfg-in", "4yzu_chains-a-b") is ONE token.
//     "/" is a DELIMITER -- it separates a "project/case" path (e.g. "F9/4YZU_chains-A-B")
//     so the case alone ("-4yzu_chains-a-b") or the project ("-f9") each match, and the
//     full path ("-f9/4yzu_chains-a-b") matches its two consecutive tokens. Matching is
//     literal apart from case/accents (separators are NOT interchangeable). A PARTIAL like
//     "-in" or "-4yzu" excludes nothing -- you type the whole label. (Like Google:
//     exclusion is word-level; we treat the hyphenated identifier as the "word".)
// This is the first increment toward the chips/faceted search described in the ticket.

// Lower-case + strip accents, matching MUI createFilterOptions' default normalization
// (ignoreCase + ignoreAccents) so existing matches behave the same. \p{Mn} removes the
// combining marks left behind by NFD decomposition (so accented letters fold to ASCII).
function normalizeForSearch(str) {
    return String(str)
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Mn}/gu, '');
}

// Token = a maximal run of identifier chars: letters, digits, and the project/case-name
// separators "_ . + -" (ProjectNameRegex). Everything else -- whitespace, "/", comma,
// colon -- is a delimiter; "/" splits a "project/case" path so each part is its own token.
// "user data f9/4yzu_chains-a-b: dfg-in" -> ["user","data","f9","4yzu_chains-a-b","dfg-in"].
function tokenize(str) {
    return str.split(/[^a-z0-9_.+-]+/).filter(Boolean);
}

// A negative "-term" excludes a row when its token(s) appear as a CONSECUTIVE run of the
// row's tokens (the term is tokenized the same way). A single identifier ("-dfg-in",
// "-4yzu_chains-a-b") is one token and must match a WHOLE token -- a PARTIAL like "-4yzu"
// or "-in" excludes nothing. A "project/case" path ("-f9/4yzu_chains-a-b") splits on "/"
// into two tokens that must match consecutively. Separators are literal (only case/accents
// are folded), so "4yzu_chains-a-b" does NOT match a "..._052525" name.
function negativeHitsTokens(term, tokens) {
    const want = tokenize(term);
    if (want.length === 0) return false;
    for (let i = 0; i + want.length <= tokens.length; i += 1) {
        if (want.every((w, j) => tokens[i + j] === w)) {
            return true;
        }
    }
    return false;
}

// Parse the query into positive (substring) terms and negative (whole-token) terms.
// "BRAF -dfg-in" -> { positive: ["braf"], negative: ["dfg-in"] }. A bare "-" is ignored.
function parseQueryTerms(inputValue) {
    const positive = [];
    const negative = [];
    normalizeForSearch(inputValue).split(/\s+/).forEach((term) => {
        if (!term || term === '-') return;
        if (term.startsWith('-')) {
            negative.push(term.slice(1));
        } else {
            positive.push(term);
        }
    });
    return { positive, negative };
}

// MUI useAutocomplete filterOptions: (options, { inputValue }) => filteredOptions.
function filterOptionsByTerms(options, { inputValue }) {
    // A non-string inputValue (e.g. undefined) means "no query" -> show everything,
    // rather than letting String(undefined) become a spurious positive term.
    if (typeof inputValue !== 'string') {
        return options;
    }
    const { positive, negative } = parseQueryTerms(inputValue);
    if (positive.length === 0 && negative.length === 0) {
        return options;
    }
    return options.filter((option) => {
        const haystack = normalizeForSearch((option.searchTerms || []).join());
        // Every positive term must appear as a substring (AND).
        if (!positive.every((term) => haystack.includes(term))) {
            return false;
        }
        // No negated term may match a consecutive run of the row's tokens.
        if (negative.length > 0) {
            const tokens = tokenize(haystack);
            if (negative.some((term) => negativeHitsTokens(term, tokens))) {
                return false;
            }
        }
        return true;
    });
}

export { filterOptionsByTerms, parseQueryTerms };
